import React, { useEffect } from "react";
import "./ViewRequestDetails.css";
import "../../../patientFullView/patientFullView.css";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Widgets from "../../../../widgets";
import Icons from "../../../../Assets/Icons";
import strings from "../../../../utils/strings";
import PatientPersonalInfo from "../../../patientFullView/patientPersonalInfo";
import AttachmentsSection from "../../manageNurseRequest/patientDetails/AttachmentsSection";
import { getNurseRequestDetails } from "../../../../modules/NursignCare/ManageRequest/manageRequestThunk";
import { buildFileUrl, getFileType } from "../../../../utils/fileHelpers";

const ViewRequestDetails = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  const { requestDetails } = useSelector((state) => state.manageRequest);

  const breadcrumbs = ["nursingCare", "manageRequests", "serviceDetails"];
  const paths = [strings.navigation.manageNurseReq, strings.navigation.manageNurseReq, strings.navigation.viewNurseReqDetails];

  useEffect(() => {
    if (id) {
      dispatch(getNurseRequestDetails({ id }));
    }
  }, [id]);

  const request = requestDetails;

  const patientData = {
    firstName: request?.patient?.fullName || "",
    lastName: "",
    hnNumber: request?.patient?.hnNumber,
    gender: request?.patient?.gender,
    dateOfBirth: request?.patient?.dateOfBirth,
    phoneNumber: request?.patient?.phoneNumber,
    countryCode: request?.patient?.countryCode,
    email: request?.patient?.email,
    insuranceCategory: request?.patient?.insuranceCategory,
    profilePhoto: request?.patient?.profilePhoto,
  };

  const formatAddress = (addr) => {
    if (!addr) return "-";
    const parts = [addr.street, addr.ward, addr.city, addr.province, addr.postalCode].filter(Boolean);
    return parts.join(", ");
  };

  const mapAttachments = (attachments) => {
    return (attachments || []).map((attachment) => {
      const rawUrl = typeof attachment === "string"
        ? attachment
        : attachment?.url || attachment?.path || attachment?.fileUrl || attachment?.filePath || attachment?.location || "";
      const name = typeof attachment === "string"
        ? rawUrl.split("/").pop().split("?")[0]
        : attachment?.name || attachment?.fileName || attachment?.originalName || rawUrl.split("/").pop().split("?")[0] || "file";
      const mimeType = typeof attachment === "string" ? "" : attachment?.mimeType || attachment?.mimetype || attachment?.contentType || attachment?.type;
      return { type: getFileType({ name, mimeType }), url: buildFileUrl(rawUrl), name };
    });
  };

  return (
    <div className="pdl-container service-details-wrapper">
      <Widgets.BreadCrumb breadcrumbs={breadcrumbs} paths={paths} />

      {/* Header */}
      <div className="department-heading pdl">
        <div>{t("serviceDetails")}</div>
        <div className="pdl-buttons">
          <div className="pdl-btn verified pav-status" style={{ backgroundColor: "#e6fffa", color: "#00b894", border: "none" }}>
            {request?.status ? t(request.status) : "-"}
          </div>
          <span className="req-id-text">Request ID: {request?.nursingRequestID || id}</span>
        </div>
      </div>

      {/* Patient Info */}
      <div className="pdl-info">
        <PatientPersonalInfo data={patientData} />

        {/* Address + Zone */}
        <div className="pdl-body extra-patient-fields">
          <div className="extra-row address-zone">
            <div className="addr-field">
              <label>
                <img src={Icons.Location} alt="loc" width="16" />
                {t("patientAddress")}
              </label>
              <div className="addr-box">
                <input type="checkbox" readOnly checked={request?.address?.defaultAddress || false} />
                <span>{formatAddress(request?.address)}</span>
              </div>
            </div>
            <div className="zone-field">
              <label className="required">{t("zone")}</label>
              <Widgets.SelectDropdown value={request?.address?.zone || ""} placeholder={t("select")} disabled big />
            </div>
          </div>
        </div>
      </div>

      <AttachmentsSection attachments={mapAttachments(request?.attachments)} />

      {/* Description */}
      <div className="pdl-info">
        <div className="notes-container no-bg-border">
          <div className="green-bar"></div>
          <div className="notes-content">
            <h4 className="notes-title" style={{ color: "#2d3436" }}>
              {t("description")}
            </h4>
            <p className="notes-text">{request?.description || "-"}</p>
          </div>
        </div>
      </div>

      {/* Services */}
      {request?.services?.length > 0 && (
        <div className="pdl-info">
          <p className="pdl-title">{t("services")}</p>
          {request.services.map((svc, idx) => (
            <div key={idx} className="pdl-body">
              <div className="service-type-main2">
                <div className="service-input-box">
                  <span>Service Group</span>
                  <input type="text" className="form-section-input-2" value={svc.service?.title?.en || "-"} readOnly />
                </div>
                <div className="service-input-box">
                  <span>Sub-Service Group</span>
                  <input type="text" className="form-section-input-2" value={svc.subService?.title?.en || "-"} readOnly />
                </div>
                <div className="service-input-box">
                  <span>Service Item</span>
                  <input type="text" className="form-section-input-2" value={svc.serviceItem?.title?.en || "-"} readOnly />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Nurse Preference */}
      <div className="pdl-info">
        <p className="pdl-title">{t("nursePreference")}</p>
        <div className="pdl-body flex-row">
          <div className="pref-box">
            <span className="pref-lbl">{t("language")}</span>
            <span className="pref-val">{request?.languagePreferences?.length > 0 ? request.languagePreferences.join(", ") : "-"}</span>
          </div>
          <div className="pref-box">
            <span className="pref-lbl">{t("gender")}</span>
            <span className="pref-val">{request?.genderPreferences?.length > 0 ? request.genderPreferences.join(", ") : "-"}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewRequestDetails;
